// Sport rules: the table from the carousel, editable. One row per move, one
// column per art, each cell the move's standing in that sport. Click a cell to
// rotate it — legal, then each condition in the art's rotation, then banned,
// then legal again. Right-click a cell to type a condition; it joins the
// rotation for next time. Techniques attach to a row by name (or by their own
// `rule` field, set in the info modal); the count on each row says how many.
// Admin only: the button that opens this is gated in Settings, and the
// Firestore rules behind it check again.
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Check, Ban, TriangleAlert, Trash2, Pencil } from 'lucide-react';
import { useTechniques } from '../hooks/useTechniques';
import { ARTS } from '../data/arts';
import { Art } from '../types';
import { BANNED, addPreset, deleteRule, rotate, ruleFor, ruleIdFor, saveRule, useRules, usePresets, type Rule } from '../utils/legality';

/** Column heads: the art, and the federation whose rulebook the column
 *  follows. Short enough to sit three across on a phone. */
const COL_LABEL: Partial<Record<Art, { art: string; fed: string }>> = {
  [Art.Bjj]: { art: 'BJJ', fed: 'IBJJF' },
  [Art.Judo]: { art: 'Judo', fed: 'IJF' },
  [Art.Wrestling]: { art: 'Wrestling', fed: 'UWW' },
};

interface SportRulesModalProps {
  onClose: () => void;
}

export default function SportRulesModal({ onClose }: SportRulesModalProps) {
  const rules = useRules();
  const presets = usePresets();
  const { techniques } = useTechniques();
  const [err, setErr] = useState('');
  // A cell shows what was clicked until the snapshot lands, so a fast run of
  // clicks reads as one motion.
  const [pending, setPending] = useState<Map<string, Rule['legality']>>(new Map());

  /** How many techniques each row covers, by the same matching the app uses. */
  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const t of techniques) {
      const r = ruleFor(t, rules);
      if (r) m.set(r.id, (m.get(r.id) ?? 0) + 1);
    }
    return m;
  }, [techniques, rules]);

  const fail = (msg: string, e: unknown) => { console.error(msg, e); setErr(msg); };

  const setCell = async (r: Rule, art: Art, value: string | undefined) => {
    const next = { ...(pending.get(r.id) ?? r.legality) };
    if (value) next[art] = value; else delete next[art];
    setPending((p) => new Map(p).set(r.id, next));
    setErr('');
    try { await saveRule({ id: r.id, legality: next }); }
    catch (e) { fail('Could not save. Try again.', e); setPending((p) => { const m = new Map(p); m.delete(r.id); return m; }); }
  };

  const typeCondition = async (r: Rule, art: Art) => {
    const cur = (pending.get(r.id) ?? r.legality)[art];
    const text = window.prompt(`Condition for ${r.name} in ${ARTS.find((a) => a.id === art)?.label}:`, cur && cur !== BANNED ? cur : '');
    if (text == null) return;
    const v = text.trim();
    await setCell(r, art, v || undefined);
    if (v && v !== BANNED && !(presets[art] ?? []).includes(v)) {
      try { await addPreset(presets, art, v); } catch (e) { console.error('Failed to add preset:', e); }
    }
  };

  const addToRotation = async (art: Art) => {
    const text = window.prompt(`New condition for ${ARTS.find((a) => a.id === art)?.label} (joins the rotation):`, '');
    if (!text?.trim()) return;
    try { await addPreset(presets, art, text); } catch (e) { fail('Could not add the condition.', e); }
  };

  const addMove = async () => {
    const name = window.prompt('Move to add (as it should read in the table):', '');
    if (!name?.trim()) return;
    const id = ruleIdFor(name);
    if (rules.some((r) => r.id === id)) { setErr('That move is already in the table.'); return; }
    try { await saveRule({ id, name: name.trim(), aliases: [], except: [], legality: {} }); } catch (e) { fail('Could not add the move.', e); }
  };

  const editMove = async (r: Rule) => {
    const text = window.prompt(
      `Names for "${r.name}", comma-separated. The first is the row's name; the rest are other names a technique might use. Start a name with - to exclude it (e.g. -twister hook).`,
      [r.name, ...r.aliases, ...r.except.map((x) => `-${x}`)].join(', '),
    );
    if (text == null) return;
    const parts = text.split(',').map((s) => s.trim()).filter(Boolean);
    const [name, ...rest] = parts.filter((s) => !s.startsWith('-'));
    const except = parts.filter((s) => s.startsWith('-')).map((s) => s.slice(1).trim()).filter(Boolean);
    if (!name) return;
    try { await saveRule({ id: r.id, name, aliases: rest, except }); } catch (e) { fail('Could not save the names.', e); }
  };

  const removeMove = async (r: Rule) => {
    if (!window.confirm(`Remove "${r.name}" from the table? Techniques under it go back to legal.`)) return;
    try { await deleteRule(r.id); } catch (e) { fail('Could not remove the move.', e); }
  };

  const cell = (r: Rule, art: Art) => {
    const v = (pending.get(r.id) ?? r.legality)[art];
    const cls = v === BANNED ? 'sr-cell--banned' : v ? 'sr-cell--cond' : 'sr-cell--legal';
    return (
      <button
        type="button"
        key={art}
        className={`sr-cell ${cls}`}
        title={v === BANNED ? 'Banned. Click to rotate; right-click to type a condition.' : v ? `${v}. Click to rotate; right-click to edit.` : 'Legal. Click to rotate; right-click to type a condition.'}
        onClick={() => setCell(r, art, rotate(v, presets[art] ?? []))}
        onContextMenu={(e) => { e.preventDefault(); typeCondition(r, art); }}
      >
        {v === BANNED ? <><Ban size={13} /> Banned</> : v ? <><TriangleAlert size={13} /> {v}</> : <Check size={13} />}
      </button>
    );
  };

  return createPortal(
    <div className="frame-modal-backdrop" onClick={onClose}>
      <div className="gift-modal sr-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="code-table-head" style={{ marginBottom: 10 }}>
          <span>Sport rules</span>
          <span className="code-table-count">{rules.length} moves</span>
        </div>
        <p className="sr-help">
          The major federations' rulebooks, one column each. Click a cell to rotate legal, each condition, then banned;
          right-click to type a condition. Techniques fall under a row when its name appears in theirs; the info modal can pin one.
        </p>
        {err && <span className="gift-modal-error user-note">{err}</span>}
        <div className="sr-table">
          <div className="sr-row sr-row--head">
            <span className="sr-name">Move</span>
            {ARTS.map((a) => (
              <span key={a.id} className="sr-art">
                <span className="sr-art-name">
                  {COL_LABEL[a.id]?.art ?? a.shortLabel}
                  <button type="button" className="sr-add" title={`Add a ${a.label} condition to the rotation`} onClick={() => addToRotation(a.id)}>
                    <Plus size={12} />
                  </button>
                </span>
                {COL_LABEL[a.id] && <span className="sr-art-fed">{COL_LABEL[a.id]!.fed}</span>}
              </span>
            ))}
          </div>
          {rules.map((r) => (
            <div className="sr-row" key={r.id}>
              <span className="sr-name">
                <span className="sr-name-text">{r.name}</span>
                <span className="sr-name-sub">
                  {counts.get(r.id) ?? 0} clip{(counts.get(r.id) ?? 0) === 1 ? '' : 's'}
                  <button type="button" className="sr-mini" title="Rename, or add other names" onClick={() => editMove(r)}><Pencil size={11} /></button>
                  <button type="button" className="sr-mini" title="Remove from the table" onClick={() => removeMove(r)}><Trash2 size={11} /></button>
                </span>
              </span>
              {ARTS.map((a) => cell(r, a.id))}
            </div>
          ))}
          {rules.length === 0 && <div className="sr-empty">No moves yet.</div>}
        </div>
        <button type="button" className="settings-modal-btn sr-add-move" onClick={addMove}>
          <Plus size={16} />
          Add a move
        </button>
      </div>
    </div>,
    document.querySelector('.phone-frame') ?? document.body,
  );
}
